import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { StarRating } from "@/components/StarRating";
import ReviewDialog from "@/components/ReviewDialog";
import { MapPin, Calendar, MessageSquare, User } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JudgeProfilePage() {
  const { id } = useParams();
  const [judge, setJudge] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewDialog, setShowReviewDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [judgeRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/judges/${id}`),
        axios.get(`${API}/judges/${id}/reviews`),
      ]);
      setJudge(judgeRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error("Failed to fetch judge data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-off-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse space-y-6">
            <div className="h-64 bg-white rounded-sm" />
            <div className="h-48 bg-white rounded-sm" />
          </div>
        </div>
      </div>
    );
  }

  if (!judge) {
    return (
      <div className="min-h-screen bg-off-white py-16 text-center">
        <h2 className="font-heading text-2xl text-navy">Judge not found</h2>
      </div>
    );
  }

  const ratingCategories = [
    { label: "Fairness", value: judge.avg_fairness },
    { label: "Knowledge", value: judge.avg_knowledge },
    { label: "Communication", value: judge.avg_communication },
    { label: "Professionalism", value: judge.avg_professionalism },
  ];

  return (
    <div className="min-h-screen bg-off-white py-8" data-testid="judge-profile-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Judge Header */}
        <div className="bg-white gold-accent-top shadow-lg rounded-sm overflow-hidden mb-8">
          <div className="p-8">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-3">
                  {judge.name}
                </h1>
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <MapPin className="w-5 h-5" />
                  <span>{judge.location || "Location not specified"}</span>
                </div>
                {judge.events_judged && judge.events_judged.length > 0 && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="w-5 h-5" />
                    <span>{judge.events_judged.join(", ")}</span>
                  </div>
                )}
              </div>
              <div className="text-center md:text-right">
                <div className="mb-2">
                  <StarRating rating={judge.avg_overall || 0} size="lg" />
                </div>
                <p className="text-slate-600">
                  {judge.review_count || 0} {judge.review_count === 1 ? "review" : "reviews"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Rating Categories */}
        {judge.review_count > 0 && (
          <div className="bg-white shadow rounded-sm p-6 mb-8">
            <h2 className="font-heading text-xl font-semibold text-navy mb-6">
              Rating Breakdown
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {ratingCategories.map((cat) => (
                <div key={cat.label} className="text-center">
                  <p className="text-sm text-slate-500 uppercase tracking-wide mb-2">
                    {cat.label}
                  </p>
                  <div className="flex justify-center">
                    <StarRating rating={cat.value || 0} size="sm" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reviews Section */}
        <div className="bg-white shadow rounded-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-semibold text-navy">
              Reviews
            </h2>
            <Button
              onClick={() => setShowReviewDialog(true)}
              className="bg-gold text-navy hover:bg-gold/90 font-semibold"
              data-testid="write-review-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Write a Review
            </Button>
          </div>

          {reviews.length > 0 ? (
            <div className="space-y-6">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="bg-slate-50 p-6 rounded-sm border-l-4 border-gold"
                  data-testid={`review-${review.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-navy rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-navy">
                          {review.display_name}
                        </p>
                        <p className="text-sm text-slate-500">
                          {formatDistanceToNow(new Date(review.created_at), {
                            addSuffix: true,
                          })}
                        </p>
                      </div>
                    </div>
                    <StarRating rating={review.overall_rating} size="sm" />
                  </div>

                  {review.comment && (
                    <p className="text-slate-700 mb-4">{review.comment}</p>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">Fairness:</span>
                      <span className="font-semibold">{review.fairness}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">Knowledge:</span>
                      <span className="font-semibold">{review.knowledge}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">Communication:</span>
                      <span className="font-semibold">{review.communication}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">Professionalism:</span>
                      <span className="font-semibold">{review.professionalism}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-slate-50 rounded-sm">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-lg text-navy mb-2">
                No reviews yet
              </h3>
              <p className="text-slate-600 mb-4">
                Be the first to share your experience with this judge!
              </p>
              <Button
                onClick={() => setShowReviewDialog(true)}
                className="bg-navy hover:bg-navy/90"
              >
                Write a Review
              </Button>
            </div>
          )}
        </div>
      </div>

      <ReviewDialog
        open={showReviewDialog}
        onOpenChange={setShowReviewDialog}
        judgeId={id}
        judgeName={judge.name}
        onSuccess={fetchData}
      />
    </div>
  );
}
